import { isNil } from "lodash-es";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { Button } from "@/components/Button";
import { Header } from "@/components/Header";
import { Text } from "@/components/Text";
import { UserContext } from "@/components/User";
import {
  deleteConsultantProfile,
  fetchConsultantProfileState,
  IConsultantProfile,
  TDeleteConsultantProfileCodes,
  TPersonalConsultantProfileCodes,
  TUnableToDeleteConsultantProfileCodes,
} from "@/data/consultant-profiles";
import { refresh } from "@/data/session";
import { fetchProfilesConnectivityToApplicationsAndContracts } from "@/data/users";
import { Flex, IDialogModal, inDialogModal } from "@/eworkitComponents";
import { SessionContext } from "@/infrastructure/Session";
import { CloseButton } from "@/views/components/CloseButton";
import { StyledModal } from "@/views/components/Modal";
import { useToasts } from "@/views/components/Toast/useToasts";
import { router } from "@/views/router";

import "./_settings-modal.scss";

interface IDeleteModalProps extends IDialogModal {
  payload: IConsultantProfile;
  onClose: (shouldRefresh?: boolean) => void;
}

interface IModalProps extends IDialogModal {
  companyName: string;
  onDelete: () => void;
  profileState: TDeleteConsultantProfileCodes;
  isPending: boolean;
}

const Modal: React.FC<IModalProps> = ({ companyName, profileState, onClose, onDelete, isPending }) => {
  const { t } = useTranslation("session");
  const messageState = isPending ? "PENDING" : (profileState as string);
  const profileType = useMemo(() => {
    if (profileState in TPersonalConsultantProfileCodes) {
      return "PERSONAL_PROFILE";
    }

    return "COMPANY_PROFILE";
  }, []);
  const renderButtons = () => {
    if (profileState in TUnableToDeleteConsultantProfileCodes) {
      return (
        <Button onClick={() => onClose()} size="lg" testName="understand">
          <Text>{t("CONSULTANT_PROFILES.DETAIL.DELETE.BUTTON_UNDERSTAND")}</Text>
        </Button>
      );
    }

    return (
      <>
        <Button variant="secondary" onClick={() => onClose()} size="lg" testName="cancel">
          <Text>{t("CONSULTANT_PROFILES.DETAIL.DELETE.BUTTON_CANCEL")}</Text>
        </Button>
        <Button onClick={onDelete} size="lg" testName="delete">
          <Text>{t(`CONSULTANT_PROFILES.DETAIL.DELETE.BUTTON_CONFIRM.${profileType}`)}</Text>
        </Button>
      </>
    );
  };

  return (
    <StyledModal>
      <Flex direction="column" padding={["lg"]} innerMargin="lg">
        <Flex justify="space-between">
          <Header weight="bold">{t(`CONSULTANT_PROFILES.DETAIL.DELETE.HEADER.${profileType}`)}</Header>
          <CloseButton onClick={onClose} testName="close" />
        </Flex>
        <Flex justify="center">
          <div className="modal__img-container" />
        </Flex>
        <Flex direction="column" innerMargin="md">
          <Text>{t(`CONSULTANT_PROFILES.DETAIL.DELETE.MESSAGES.${messageState}.HEADER`, { value: companyName })}</Text>
          <Text>
            <Trans
              ns="session"
              values={{ value: companyName }}
              i18nKey={`CONSULTANT_PROFILES.DETAIL.DELETE.MESSAGES.${messageState}.TEXT`}>
              <Text weight="bold" />
            </Trans>
          </Text>
          <Flex justify="flex-end" innerMargin="md">
            {renderButtons()}
          </Flex>
        </Flex>
      </Flex>
    </StyledModal>
  );
};

const DeleteModalComponent = inDialogModal(Modal);

export const DeleteModal: React.FC<IDeleteModalProps> = React.memo(({ open, payload, onClose }) => {
  const { addToast } = useToasts();
  const history = useHistory();
  const Session = useContext(SessionContext);
  const User = useContext(UserContext);
  const [profileState, setProfileState] = useState<TDeleteConsultantProfileCodes>();
  const { t } = useTranslation(["session", "common"]);

  const {
    contextCompany,
    contextConsultantProfileId,
    getFirstAvailableProfile,
    setConsultantProfileContext,
    hasCompanyManagerPrivileges,
  } = User;

  const companyName = (payload.sharedProfile && payload.sharedProfile.company.name) || "";

  useEffect(() => {
    if (open && isNil(profileState)) {
      handleProfileState();
    }
  }, [open]);

  const handleProfileState = useCallback(async () => {
    try {
      const response = await fetchConsultantProfileState({
        companyId: contextCompany && contextCompany.id,
        consultantProfileId: payload.id,
      });
      setProfileState(response.state);
    } catch (response) {
      addToast({
        text: t("common:ERROR_MESSAGES.GENERAL"),
        type: "warning",
      });
      onClose();
    }
  }, [profileState]);

  const handleDeleteProfile = async () => {
    try {
      const connectivityRes = await fetchProfilesConnectivityToApplicationsAndContracts(payload.id);

      if (connectivityRes.hasActiveAppOrContract) {
        addToast({
          text: t("CONSULTANT_PROFILES.DETAIL.DELETE.MESSAGES.PROFILE_HAS_ACTIVE_APPLICATION"),
          type: "warning",
        });
        return;
      }

      await deleteConsultantProfile({
        consultantProfileId: payload.id,
        companyId: contextCompany && contextCompany.id,
      });

      if (contextConsultantProfileId === payload.id) {
        const availableProfile = getFirstAvailableProfile(contextConsultantProfileId);
        if (!availableProfile) {
          throw new Error("No available profile found.");
        }

        const isManager = hasCompanyManagerPrivileges();

        if (isManager) {
          history.push(router.get("companies.team").path);
        } else {
          history.push(
            router.get("consultant-profiles.detail", {
              consultantProfileId: availableProfile.id,
            }).path,
          );
        }

        setConsultantProfileContext(undefined);
      } else {
        const isManager = hasCompanyManagerPrivileges();

        if (isManager) {
          history.push(router.get("companies.team").path);
        }
      }

      const sessionUser = await refresh();

      Session.update(sessionUser, () => {
        addToast({
          text: t("CONSULTANT_PROFILES.DETAIL.DELETE.MESSAGES.SUCCESS"),
          type: "success",
        });
      });
    } catch {
      addToast({
        text: t("common:ERROR_MESSAGES.GENERAL"),
        type: "warning",
      });
    } finally {
      onClose(true);
    }
  };

  if (open && !isNil(profileState)) {
    return (
      <DeleteModalComponent
        companyName={companyName}
        onClose={onClose}
        onDelete={handleDeleteProfile}
        open={open}
        profileState={profileState!}
        isPending={payload.status === "PENDING"}
        size="xs"
      />
    );
  }

  return null;
});
